import { max } from 'lodash';
import { cast, types as t } from 'mobx-state-tree';
import { useCreateStore, useProvider, useStore } from 'mobx-store-provider';
import * as React from 'react';

import { findNodeByElementId } from '../../shared-transforms';
import { CardLogicTransformActions } from './transforms';

export const getInitializeCardLogicStore = () => {
  return {};
};

const cardLogicSelectionInitialState = {
  headerCellNodeElementId: '',
  columnNodeElementId: '',
  cellNodeElementId: '',
};

const CardLogicTableInfoSelection = t.model(
  'CardLogicTableInfoSelection',
  cardLogicSelectionInitialState
);

const CardLogicColumnInfo = t.model('CardLogicColumnInfo', {
  id: t.maybeNull<any>(t.string),
  rowCount: t.optional(t.number, 0),
  elementIds: t.optional(t.array(t.string), []),
});

export const CardLogicStore = t
  .model('CardLogicStore', {
    id: t.maybe<any>(t.string),

    isInCardLogic: t.optional(t.boolean, false),
    currentSelection: t.optional(CardLogicTableInfoSelection, cardLogicSelectionInitialState),
    columnCount: t.optional(t.number, 0),
    colInfo: t.optional(t.array(CardLogicColumnInfo), []),

    headerHeights: t.array(t.number),

    deleteButtonHovering: t.optional(t.boolean, false),
  })
  .views((self) => ({
    get maxHeaderHeight() {
      return max(self.headerHeights.map((n) => n + 11));
    },
  }))
  .actions((self) => ({
    hoverDeleteButton() {
      self.deleteButtonHovering = true;
    },
    stopHoverDeleteButton() {
      self.deleteButtonHovering = false;
    },
    increaseRowCount() {
      // simulate all row count
      // the row count correctness will belong to the selectionchange sync
      self.colInfo.replace(
        self.colInfo.map((col) => {
          col.rowCount += 1;
          return col;
        })
      );
    },
    decreaseRowCount() {
      // simulate all row count
      // the row count correctness will belong to the selectionchange sync
      self.colInfo.replace(
        self.colInfo.map((col) => {
          col.rowCount -= 1;
          return col;
        })
      );
    },
  }))
  .actions((self) => ({
    setId(id) {
      self.id = id;
    },
    retrieveTableInfo({ editor }) {
      const elementId = self.id;
      const tableInfo = CardLogicTransformActions.retrieveTableInfoByElementId({
        editor,
        elementId,
      });
      const { isInCardLogic, currentSelection, colInfo, columnCount } = tableInfo;
      self.isInCardLogic = isInCardLogic;
      if (currentSelection) {
        self.currentSelection = cast(currentSelection);
      } else {
        self.currentSelection = cardLogicSelectionInitialState;
      }
      self.columnCount = columnCount;
      if (colInfo) {
        self.colInfo.replace(colInfo);
      }

      // update header height
      const elms = document.querySelectorAll(
        `[data-element-id="${elementId}"] .card-logic-header-cell-element`
      );
      if (elms) {
        self.headerHeights.replace(
          Array.from(elms).map((elm: any) => {
            return elm.firstChild.firstChild.firstChild.offsetHeight as number;
          })
        );
      }
    },
    insertColumnNext({ editor, elementId }) {
      CardLogicTransformActions.insertColummByElementId({
        editor,
        elementId,
      });
      self.columnCount += 1;
    },
    removeColumn({ editor, elementId }) {
      CardLogicTransformActions.removeColumnByElementId({
        editor,
        elementId,
      });
      self.columnCount -= 1;
    },
    addCardBefore({ editor, elementId }) {
      CardLogicTransformActions.insertCardByElementId({
        editor,
        elementId,
        insertLocation: 'up',
      });
      self.increaseRowCount();
    },
    addCardAfter({ editor, elementId }) {
      CardLogicTransformActions.insertCardByElementId({
        editor,
        elementId,
        insertLocation: 'down',
      });
      self.increaseRowCount();
    },
    removeCard({ editor, elementId }) {
      CardLogicTransformActions.deleteCardByElementId({
        editor,
        elementId,
      });
      self.decreaseRowCount();
    },
    checkDisableRemoveCard({ editor, elementId }) {
      findNodeByElementId({ editor, elementId });
    },
    setFocusColumnId(colId) {
      self.currentSelection.columnNodeElementId = colId;
    },
  }))
  .views((self) => ({
    isDisableRemoveCard() {
      const info = self.colInfo.find(
        (col) => col.id === self?.currentSelection?.columnNodeElementId
      );
      if (info) {
        return info.rowCount <= 1;
      }
      return false;
    },
  }));

export const useCardLogicStore = () => {
  return useStore(CardLogicStore);
};

export const withCardLogicStore = (Component: any) => (props: any) => {
  const cardLogicCreateStore = useCreateStore(CardLogicStore, getInitializeCardLogicStore());
  const CardLogicStoreProvider = useProvider(CardLogicStore);

  return (
    <CardLogicStoreProvider value={cardLogicCreateStore}>
      <Component {...props} />
    </CardLogicStoreProvider>
  );
};
